import React from 'react'
import { CloseCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'
import { useHistory } from 'react-router'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import TabIcon from './TabIcon'
import OverflowText from '@SonarCX/components/atoms/OverflowText'

import { Routes } from '@SonarCX/Routes'
import {
  PinnedTab as PinnedTabType,
  removeTab
} from '@SonarCX/slices/pinnedTabs'
import { useNoteVisibility } from '@SonarCX/providers/NoteVisibilityProvider'
import useStyles from './styles'

type PinnedTabProps = {
  tab: PinnedTabType
  isLast: boolean
}

const PinnedTab: React.FC<PinnedTabProps> = ({ tab, isLast }) => {
  const theme = useTheme()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const { currentNote, setCurrentNote, setOpen } = useNoteVisibility()

  const classes = useStyles()

  const handleOpenNoteClick = React.useCallback(
    (tab: PinnedTabType) => {
      const updateNote = async () => {
        setCurrentNote({
          ...tab,
          content: tab.content
        })
        dispatch(removeTab(tab.id))
        setOpen(true)
      }

      if (currentNote === null) {
        updateNote()
        return
      }

      // Need to figure out how to update lexical text, meanwhile just hide the form then show it again.
      setCurrentNote(null)
      setTimeout(updateNote, 1)
    },
    [currentNote, setCurrentNote, setOpen, dispatch]
  )

  const handleOpenTeamChatClick = React.useCallback(
    (tab: PinnedTabType) => {
      const path = `${Routes.PEER_CHAT}?session=${tab.id}${
        tab.isGroup ? '' : '&isUser=true'
      }`
      history.push(path)
    },
    [history]
  )

  const handleOpenTaskClick = React.useCallback(
    (tab: PinnedTabType) => {
      history.push(`${Routes.TASKS}/${tab.id}`)
    },
    [history]
  )

  const handleOpenContactClick = React.useCallback(
    (tab: PinnedTabType) => {
      history.push(`${Routes.CUSTOMERS}/${tab.id}`)
    },
    [history]
  )

  const handleOpenTeamMemberClick = React.useCallback(
    (tab: PinnedTabType) => {
      history.push(`${Routes.USERS}/${tab.id}`)
    },
    [history]
  )

  const handleOpenArticleClick = React.useCallback(
    (tab: PinnedTabType) => {
      history.push(`${Routes.DOCUMENTS}/${tab.id}`)
    },
    [history]
  )

  const handleOpenTabClick = React.useCallback(
    async (tab: PinnedTabType) => {
      const type = tab.type

      if (type === 'note') {
        handleOpenNoteClick(tab)
        return
      }

      if (type === 'team-chat') {
        handleOpenTeamChatClick(tab)
        return
      }

      if (type === 'task') {
        handleOpenTaskClick(tab)
        return
      }

      if (type === 'contact') {
        handleOpenContactClick(tab)
        return
      }

      if (type === 'team-member') {
        handleOpenTeamMemberClick(tab)
        return
      }

      if (type === 'article') {
        handleOpenArticleClick(tab)
        return
      }
    },
    [
      handleOpenNoteClick,
      handleOpenTeamChatClick,
      handleOpenTaskClick,
      handleOpenContactClick,
      handleOpenTeamMemberClick,
      handleOpenArticleClick
    ]
  )

  const handleRemoveTab = React.useCallback(
    (e, tabID: string) => {
      e.stopPropagation()
      dispatch(removeTab(tabID))
    },
    [dispatch]
  )

  return (
    <div
      onClick={() => handleOpenTabClick(tab)}
      className={classes.root}
      style={{
        marginRight: isLast ? '0' : '0.5rem'
      }}
    >
      <TabIcon type={tab.type} />

      <OverflowText className={classes.title}>{tab.title}</OverflowText>

      <CloseCircle
        onClick={e => handleRemoveTab(e, tab.id)}
        size={theme.size.sm}
        color='black'
        className={classes.closeBtn}
      />
    </div>
  )
}

export default PinnedTab
